<template>
  <div
    ref="component"
    class="wetheme-dropdown"
    :class="[
      'fit-' + fit,
      open ? 'wetheme-dropdown--open' : 'wetheme-dropdown--closed',
      'wetheme-dropdown--' + direction,
    ]"
  >
    <label v-if="title" :for="id" v-text="title"></label>
    <div class="wetheme-dropdown__wrapper">
      <button
        :id="id"
        ref="button"
        type="button"
        autocomplete="off"
        readonly="true"
        tabindex="0"
        :aria-controls="dropdownId"
        :aria-expanded="open"
        @click="toggleOpen"
        @blur.prevent="close"
        @keydown.up.prevent="moveFocusedIndex(-1)"
        @keydown.down.prevent="moveFocusedIndex(1)"
        @keydown.enter.prevent="applyFocused(false)"
        @keydown.space.prevent="applyFocused(false)"
        @keydown.esc.prevent="close"
      >
        <span
          v-text="selectedOption.label || '\xa0'"
          class="header--supporting-text"
        ></span>
        <span class="icon-dropdown">
          <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 448 512" 
          width="24" 
          height="24" 
          fill="currentColor" 
          stroke="none"
          class="chevron-down">
            <!--! Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
            <path d="M206 365.3l22.6-22.6 160-160L411.3 160 366 114.7l-22.6 22.6L206 274.7 68.6 137.4 46 114.7 .7 160l22.6 22.6 160 160L206 365.3z"/>
          </svg>
        </span>
      </button>
      <transition name="wetheme-dropdown-fade">
        <ul :id="dropdownId" ref="ul" v-show="open">
          <li
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :class="{ selected: option.value === options[focusedIndex].value }"
            @mousedown="optionClicked(option)"
            v-text="option.label"
          ></li>
        </ul>
      </transition>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

function getElementWidth(el) {
  const clone = el.cloneNode(true);
  clone.style.cssText =
    "position: fixed; top: 0; left: 0; overflow: auto; visibility: hidden; pointer-events: none; height: unset; max-height: unset";
  document.body.appendChild(clone);
  const width = clone.getBoundingClientRect().width;
  document.body.removeChild(clone);
  return width;
}

let dropdown_last_id = 0;

export default Vue.extend({
  props: {
    fit: {
      type: String,
      default: "parent",
      validator: function (value) {
        return ["parent", "current", "longest"].indexOf(value) !== -1;
      },
    },
    direction: {
      type: String,
      default: "down",
      validator: function (value) {
        return ["down", "up"].indexOf(value) !== -1;
      },
    },
    name: {
      type: String,
      default: "",
    },
    value: {
      type: String,
      default: null,
    },
  },
  data: function () {
    return {
      id: "",
      title: "",
      open: false,
      options: [],
      selectedOption: undefined,
      // focusedIndex is index of option that user navigated to using keyboard,
      // it's equal to index of selected option when keyboard was not used
      focusedIndex: 0,
      selectComponent: undefined,
    };
  },
  computed: {
    dropdownId: function () {
      return this.id + "-dropdown";
    },
  },
  watch: {
    value: function (newVal) {
      this.options.forEach((option, index) => {
        if (option.value === newVal) {
          this.selectedOption = option;
          this.focusedIndex = index;
        }
      });
    },
  },
  beforeMount: function () {
    // Convert a <select> and <label> into options and other properties
    const el =
      this.$root.$el.tagName === "WETHEME-DROPDOWN"
        ? this.$root.$el
        : this.$root.$el.querySelector("wetheme-dropdown");
    var select = el.querySelector("select");
    if (select.id) {
      this.id = select.id;
    } else {
      dropdown_last_id += 1;
      this.id = "wetheme-dropdown-" + dropdown_last_id;
    }
    var labelEl = el.querySelector("label");
    this.title = labelEl ? labelEl.textContent : undefined;
    var options = [];
    Array.prototype.forEach.call(
      select.querySelectorAll("option"),
      (option, index) => {
        options.push({
          value: option.value,
          label: option.textContent,
        });
        if (option.defaultSelected) {
          this.selectedOption = option;
          this.focusedIndex = index;
        }
      }
    );
    this.options = options;
    if (!this.selectedOption) {
      this.selectedOption = options[0];
      this.focusedIndex = 0;
    }
    this.selectWidth = getElementWidth(select);
    this.selectComponent = select;
    this.selectComponent.style.display = "none";
    this.selectComponent.addEventListener("input", console.log);
    el.parentNode.removeChild(el);
  },
  mounted: function () {
    if (this.fit === "longest") {
      var w = this.selectWidth + 20; // 20px for caret
      this.$el.style.width = w + "px";
      this.$refs.ul.style.width = w + "px";
    }
    this.$el.appendChild(this.selectComponent);
  },
  methods: {
    findOptionIndex: function (option) {
      var index;
      this.options.forEach(function (o, i) {
        if (o.value === option.value) {
          index = i;
        }
      });
      return index;
    },
    optionClicked: function (option) {
      this.selectedOption = option;
      this.focusedIndex = this.findOptionIndex(option);
      this.selectComponent.value = option.value;
      this.open = false;
      this.$nextTick(function () {
        // Emit the event when input value is already changed
        var e = new CustomEvent("change");
        this.selectComponent.dispatchEvent(e);
      });
      this.$emit("change", option.value, this.name);
    },
    moveFocusedIndex: function (offset) {
      var focusedIndex = this.focusedIndex + offset;
      if (focusedIndex >= 0 && focusedIndex < this.options.length) {
        this.focusedIndex = focusedIndex;
      }
      if (!this.open) {
        this.applyFocused(true);
      }
    },
    toggleOpen: function () {
      this.$nextTick(function () {
        if (!this.open) {
          this.open = true;
          this.$refs.button.focus();
        } else {
          this.open = false;
          this.$refs.button.blur();
        }
      });
    },
    applyFocused: function (force) {
      if (!this.open && !force) {
        this.open = true;
        return;
      }
      this.optionClicked(this.options[this.focusedIndex]);
      this.open = false;
    },
    close: function () {
      this.open = false;
    },
  },
});
</script>
